import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SocialReportPage } from "@/features/reports/SocialReportPage";
import {
  AUTO_GENERATE_PARAM,
  autoGenerateFromSearch,
  openFromSearch,
  recorteFromSearch
} from "@/features/reports/report-recorte";
import { readFacets, VIEW_PREFIX } from "@/shared/filters/snapshot-facets";
import { useAppShell } from "@/shell/app-shell-context";
import { useRequireCompany } from "@/shared/hooks/useRequireCompany";
import { canOperate } from "@/shared/utils/roles";

/** O escopo de LEITURA da URL: vocabulário fechado, e valor
 *  desconhecido vira "o padrão do relatório". */
function viewScopeFrom(raw: string | null): "own" | "competitors" | "all" | null {
  return raw === "own" || raw === "competitors" || raw === "all" ? raw : null;
}

export function SocialReportRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, openMention, profile } = useAppShell();
  const company = useRequireCompany();

  // "Fechar amostra" na tela de Menções manda `gerar=1`, e a página gera
  // ao chegar. O parâmetro sai da URL logo em seguida — a página já o
  // leu no monte —, porque um F5 ou o link colado pra alguém não pode
  // disparar outra geração (a trava do servidor responderia 409 com
  // cara de bug).
  const autoGenerate = autoGenerateFromSearch(searchParams);
  useEffect(() => {
    if (!autoGenerate) return;
    const next = new URLSearchParams(searchParams);
    next.delete(AUTO_GENERATE_PARAM);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate]);

  if (!company) return null;

  // O recorte que a tela de Menções mandou, já saneado — vocabulário
  // desconhecido, id de marca inventado e busca gigante são descartados
  // aqui, na entrada, e não viram 422 depois do clique em gerar.
  //
  // A tela consome isto UMA VEZ, no `useState` inicial. Funciona pelo
  // DESMONTE da rota: chegar de menções monta este componente de novo,
  // então o valor inicial é lido de novo. Mudar a querystring SEM sair
  // da rota não re-lê nada — e é o comportamento certo, porque depois
  // da primeira geração quem guarda o recorte é o rascunho, e quem o
  // herda num recálculo é o servidor.
  const recorte = recorteFromSearch(searchParams);

  // Qual relatório reabrir. Mesma convenção do dashboard, e é ela que
  // faz o F5 e o link compartilhado voltarem pro mesmo relatório — sem
  // isso, um filtro de leitura na querystring seria filtro sobre nada.
  const aberto = openFromSearch(searchParams);

  // O filtro de LEITURA, sob o prefixo `view.`. O prefixo é o que
  // impede a colisão com o recorte de GERAÇÃO logo acima, que usa
  // `from`, `to` e `minAdValue` sem prefixo — sem ele, um mínimo de
  // valoração escolhido só pra ler voltaria do F5 como recorte de
  // geração e congelaria na próxima foto.
  //
  // Não precisa sanear como o recorte: valor desconhecido aqui devolve
  // recorte vazio no servidor, que é a resposta certa e a mesma do
  // dashboard.
  const view = {
    facets: readFacets(searchParams, VIEW_PREFIX),
    from: searchParams.get(VIEW_PREFIX + "from") ?? "",
    to: searchParams.get(VIEW_PREFIX + "to") ?? "",
    scope: viewScopeFrom(searchParams.get(VIEW_PREFIX + "scope"))
  };

  // `openMention` para a lista abrir o drawer de menção que já existe —
  // o relatório não precisa de tela de detalhe própria: a publicação é
  // a mesma, só vista de outro lugar.
  return (
    <SocialReportPage
      token={token}
      companyId={company.id}
      openMention={openMention}
      initialRecorte={recorte}
      initialOpen={aberto}
      initialView={view}
      autoGenerate={autoGenerate}
      readOnly={!canOperate(profile)}
    />
  );
}
